import { IconContext, TrashSimpleIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import React from 'react'
import z from 'zod'
import { Spinner } from '../spinner.tsx'
import { BUTTON_ICON_CONTEXT } from './button-icon-context.ts'
import * as styles from './default-button.css.ts'

export const ButtonTypeSchema = z.enum(['primary', 'secondary', 'tertiary', 'destructive'])
export type ButtonType = z.infer<typeof ButtonTypeSchema>

export const ButtonStateSchema = z.enum(['default', 'disabled', 'loading'])
export type ButtonState = z.infer<typeof ButtonStateSchema>

export const ButtonSizeSchema = z.enum(['default', 'small'])
export type ButtonSize = z.infer<typeof ButtonSizeSchema>

export const ButtonRoleSchema = z.enum(['button', 'submit', 'reset'])
export type ButtonRole = z.infer<typeof ButtonRoleSchema>

type DefaultButtonProps = {
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>
  children: React.ReactNode
  buttonType?: ButtonType
  buttonRole?: ButtonRole
  state?: ButtonState
  size?: ButtonSize
  href?: string
  openInNewTab?: boolean
  className?: string
  ariaLabel?: string
  ariaDescribedBy?: string
  icon?: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => unknown
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => unknown
  loadingLabel?: string
  'data-testid'?: string
}

export const DefaultButton: React.FC<DefaultButtonProps> = ({
  ref,
  children,
  buttonType = 'primary',
  state = 'default',
  size = 'default',
  href,
  openInNewTab = true,
  className,
  onClick,
  onKeyDown,
  buttonRole = 'button',
  ariaLabel,
  ariaDescribedBy,
  icon,
  loadingLabel = 'Loading',
  'data-testid': dataTestId,
}) => {
  if (href)
    return (
      <IconContext.Provider value={BUTTON_ICON_CONTEXT}>
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classNames(styles.button({ buttonType, state, size }), className)}
          onMouseDown={e => {
            e.preventDefault()
          }}
          href={state === 'disabled' ? '#' : href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          onClick={onClick}
          onKeyDown={onKeyDown}
        >
          {children}
        </a>
      </IconContext.Provider>
    )

  const child =
    buttonType === 'destructive' ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon || <TrashSimpleIcon className={styles.trashIcon} size={16} />}
        {children}
      </div>
    ) : (
      children
    )

  return (
    <IconContext.Provider value={BUTTON_ICON_CONTEXT}>
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classNames(styles.button({ buttonType, state, size }), className)}
        disabled={state === 'disabled' || state === 'loading'}
        onMouseDown={e => {
          e.preventDefault()
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-busy={state === 'loading'}
        type={buttonRole}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-testid={dataTestId}
      >
        {state === 'loading' ? <Spinner label={loadingLabel} size={'auto'} /> : child}
      </button>
    </IconContext.Provider>
  )
}
